/**
 * Typed client for the worklist console API.
 *
 * Base URL is empty by default so requests go to the origin the console was
 * loaded from and Vite's dev proxy (or the production reverse proxy) forwards
 * /api to the backend. Set VITE_API_BASE only when the console is served from
 * a different host than the API.
 */

const BASE = import.meta.env.VITE_API_BASE ?? ''

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`${status}: ${detail}`)
    this.name = 'ApiError'
  }
}

/** Set by the auth provider so a 401 anywhere returns the whole app to login. */
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    // The session is an HttpOnly cookie, so it has to be sent explicitly:
    // fetch omits credentials by default. Without this every call is a 401 and
    // the console looks broken rather than signed out.
    credentials: 'same-origin',
    ...init,
  })

  // A 401 anywhere means the session ended — expired, revoked, or the account
  // disabled. Handled once, here, rather than by every page remembering to.
  if (response.status === 401 && !path.includes('/auth/login')) {
    onUnauthorized?.()
  }

  if (!response.ok) {
    // FastAPI puts the human-readable reason in `detail`. Pydantic validation
    // errors arrive as a list of objects instead, and rendering "[object
    // Object]" at somebody who mistyped a field is worse than useless — so
    // those are flattened into "field: message".
    let detail = response.statusText
    try {
      const body = await response.json()
      if (typeof body.detail === 'string') {
        detail = body.detail
      } else if (Array.isArray(body.detail)) {
        detail = body.detail
          .map((e: { loc?: unknown[]; msg?: string }) => {
            const field = (e.loc ?? []).filter((p) => p !== 'body').join('.')
            return field ? `${field}: ${e.msg}` : e.msg
          })
          .join('; ')
      } else {
        detail = JSON.stringify(body)
      }
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, detail)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

// --- Roles -------------------------------------------------------------------

/** The four roles, in increasing order of privilege. Cumulative. */
export const ROLES = ['viewer', 'worklist_admin', 'config_admin', 'security_admin'] as const
export type Role = (typeof ROLES)[number]

export const ROLE_LABELS: Record<Role, string> = {
  viewer: 'Viewer',
  worklist_admin: 'Worklist administrator',
  config_admin: 'Configuration administrator',
  security_admin: 'Security administrator',
}

/**
 * The split that matters is worklist_admin / config_admin. Editing a worklist
 * changes what a device SEES; editing a device changes WHICH device it is, and
 * on a multi-facility installation that is a privacy boundary.
 */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  viewer: 'Read everything. Change nothing.',
  worklist_admin: 'Worklists, rules, filters and scheduled steps.',
  config_admin: 'Also devices, facilities, listeners and sources — the access-control surface.',
  security_admin: 'Also console accounts.',
}

export function roleAllows(actual: Role | undefined, required: Role): boolean {
  if (!actual) return false
  return ROLES.indexOf(actual) >= ROLES.indexOf(required)
}

export interface CurrentUser {
  username: string
  display_name: string
  role: Role
  must_change_password: boolean
  last_login_at: string
}

export interface AppUserRow extends CurrentUser {
  id: number
  enabled: boolean
  created_at: string
  updated_at: string
}

// --- Tenancy -----------------------------------------------------------------

export interface Facility {
  id: number
  code: string
  name: string
  /** What goes on the wire as (0008,0080) when an item carries none itself. */
  dicom_institution_name: string
  enabled: boolean
  notes: string
}

export interface Department {
  id: number
  facility_id: number
  code: string
  name: string
  enabled: boolean
  notes: string
}

/** Facilities with their departments — one call, for every picker. */
export interface FacilityTree extends Facility {
  departments: Department[]
}

// --- Devices -----------------------------------------------------------------

export interface DeviceDepartment {
  id: number
  code: string
  name: string
  facility_id: number
  facility_code: string
  is_primary: boolean
}

/**
 * A modality that may ask us for a worklist.
 *
 * Identity is the PAIR `(ae_title, source_ip)`, not the title alone: two
 * hospitals legitimately have a scanner called CT1.
 *
 * `facility_id` is null when unassigned — and on a multi-facility installation
 * a device without one is served NOTHING by any tenancy-scoped rule. That is
 * the fail-closed direction and it is deliberate, but it is invisible from the
 * modality, so the console flags it.
 */
export interface Device {
  id: number
  name: string
  ae_title: string
  hostname: string
  port: number
  /** Empty is the wildcard "this AE title from anywhere". */
  source_ip: string
  device_type: string
  /** Feeds the $AE_MODALITY token. */
  modality: string
  /** Feeds $AE_STATION. */
  station_name: string
  /** Feeds $AE_LOCATION. */
  location: string
  facility_id: number | null
  facility_code: string
  enabled: boolean
  allow_echo: boolean
  allow_worklist: boolean
  notes: string
  departments: DeviceDepartment[]
  department_ids: number[]
  primary_department_id: number | null
}

// --- Listeners ---------------------------------------------------------------

/**
 * One listener. **The port is the identity**, not the AE title — the same
 * title answering on several ports is normal, and the port is the only thing
 * the daemon can use to tell which listener an association arrived on.
 */
export interface Listener {
  id: number
  name: string
  ae_title: string
  description: string
  listen_address: string
  listen_port: number
  max_associations: number
  worker_count: number
  max_pdu: number
  acse_timeout_seconds: number
  dimse_timeout_seconds: number
  accept_echo: boolean
  accept_find: boolean
  restrict_to_listed_aes: boolean
  query_policy: 'open' | 'registered' | 'listed'
  enabled: boolean
  listed_devices: { id: number; name: string; ae_title: string; source_ip: string }[]
  listed_device_ids: number[]
  /**
   * Always false. DCMTK's ASC_initializeNetwork() takes a port and no address,
   * so every listener binds every interface regardless of what is configured.
   * The console must never present the configured address as a restriction
   * that is in force.
   */
  address_enforced: boolean
  /** Set on a PATCH response: fields whose change makes the daemon rebind. */
  rebind_required?: string[]
}

// --- Sources -----------------------------------------------------------------

/**
 * What the console may see of a source's connection string.
 *
 * Never the password, and never the string itself. It holds the credentials
 * for somebody else's production RIS; rendering it into a form field would put
 * it in the browser's memory, its autofill, and any screenshot of the page.
 */
export interface ConnectionSummary {
  configured: boolean
  driver: string
  server: string
  port: string
  database: string
  username: string
  password_set: boolean
}

export interface SourceMapping {
  id: number
  source_id: number
  dicom_keyword: string
  source_column: string
  constant_value: string
  transform: 'none' | 'date' | 'time' | 'name' | 'upper'
  filterable: boolean
  notes: string
  /** False when the DICOM dictionary does not know the keyword. */
  resolvable: boolean
  in_sps: boolean | null
}

export interface SourceAlias {
  id: number
  external_code: string
  notes: string
  facility_id?: number
  facility_code?: string
  department_id?: number
  department_code?: string
}

export interface Readiness {
  level: 'error' | 'warning'
  message: string
}

export interface Source {
  id: number
  name: string
  description: string
  kind: 'local' | 'odbc'
  base_query: string
  read_uncommitted: boolean
  query_timeout_seconds: number
  max_rows: number
  facility_column: string
  department_column: string
  enabled: boolean
  notes: string
  connection: ConnectionSummary
  worklist_count: number
  mappings?: SourceMapping[]
  facility_aliases?: SourceAlias[]
  department_aliases?: SourceAlias[]
  /** Everything about this source that would refuse or spoil a query. */
  readiness?: Readiness[]
}

// --- Filters -----------------------------------------------------------------

export type MatchOp = 'eq' | 'ne' | 'in' | 'notin' | 'like' | 'range' | 'isnull' | 'notnull'

export interface Filter {
  id: number
  owner_kind: 'worklist' | 'rule'
  owner_id: number
  attribute: string
  match_op: MatchOp
  value: string
  enabled: boolean
  notes: string
  /** False means the daemon DROPS it — the owner then matches MORE rows. */
  resolvable: boolean
}

// --- Worklists ---------------------------------------------------------------

/**
 * A named, filtered view of one source: the thing a modality is given.
 *
 * The date window is a BOUND, not a default. It is applied on every query and
 * ANDed with whatever the modality asked for, so a device that sends no start
 * date gets the window and one that sends a date outside it gets nothing.
 * Without it, a modality sending an empty identifier has asked for every
 * scheduled step the institution ever recorded.
 */
/** One AE title a worklist is served to. */
export interface WorklistAe {
  id: number
  worklist_id: number
  ae_title: string
  enabled: boolean
  notes: string
  /** How many enabled devices are registered under this title. 0 = probably a typo. */
  known_devices: number
}

/**
 * A named, filtered view of one source: the thing a modality is given.
 *
 * **AE titles are the primary grouping.** A worklist is a selection of records
 * and `ae_titles` is who receives it. Rules remain for what a title list
 * cannot express — a whole subnet, the caller's own facility, a per-listener
 * override — and most sites never need one.
 */
export interface Worklist {
  id: number
  name: string
  description: string
  source_id: number
  source_name: string
  source_kind: string
  source_enabled: boolean
  /**
   * The SELECTION: which facilities'/departments' records are in this list.
   *
   * **Empty means ALL** — the opposite default to `ae_titles`, which is the
   * DISTRIBUTION and where empty means nobody. An empty selection means "not
   * narrowed"; an empty distribution means "nobody ticked it yet". Each
   * defaults in the safe direction for what it controls.
   */
  facilities: { id: number; code: string; name: string }[]
  facility_ids: number[]
  departments: { id: number; code: string; name: string; facility_code: string }[]
  department_ids: number[]
  start_offset_days: number
  end_offset_days: number
  sps_status_filter: string
  max_results: number
  enabled: boolean
  /** Served to a caller no association and no rule covers. At most one. */
  default_for_unmatched: boolean
  /**
   * Who supplies ScheduledStationAETitle (0040,0001) on the results.
   * 'data' = the order source's column · 'caller' = the receiving AE title
   * · 'fixed' = `station_ae_value`.
   *
   * Order sources often leave the field empty while modalities match on it,
   * so 'data' can mean an empty list on a day with patients booked.
   */
  station_ae_mode: 'data' | 'caller' | 'fixed'
  station_ae_value: string
  /** Answer with the whole window whatever date the caller asked for. */
  ignore_caller_date: boolean
  notes: string
  filters: Filter[]
  /** Who gets this worklist. The primary grouping. */
  ae_titles: WorklistAe[]
  rule_count: number
}

/** The association inverted: what does THIS scanner receive? */
export interface AeAssignment {
  ae_title: string
  worklists: { id: number; name: string; enabled: boolean }[]
}

// --- Rules -------------------------------------------------------------------

/**
 * Which caller sees which worklist.
 *
 * Two things that are easy to confuse and are different columns:
 *   match_*    decides whether the rule FIRES
 *   *_scope    decides what it then SERVES
 */
/** How a worklist came to be served. */
export type SelectedBy = 'ae' | 'rule' | 'default'

export interface Rule {
  id: number
  name: string
  description: string
  /** Lower runs first; ties break on id so the order is stable. */
  priority: number
  calling_ae_pattern: string
  called_ae_pattern: string
  /** exact, CIDR, glob, or a comma-separated list of those. Empty = any. */
  source_ip_pattern: string
  listener_port: number | null
  match_facility_id: number | null
  match_facility_code: string
  match_department_id: number | null
  match_department_code: string
  facility_scope: 'inherit' | 'device'
  department_scope: 'inherit' | 'device'
  worklist_id: number
  worklist_name: string
  worklist_enabled: boolean
  stop_on_match: boolean
  enabled: boolean
  notes: string
  filters: Filter[]
}

// --- Worklist preview --------------------------------------------------------

/**
 * What a worklist currently returns.
 *
 * Produced by `mwl-dicomd --preview`, which runs the daemon's own query
 * builder — not a reimplementation in the API. Rows are flat maps of DICOM
 * keyword to value, carrying only the attributes that had a value.
 *
 * ROWS CONTAIN PHI.
 */
export interface WorklistPreview {
  worklist: {
    id: number
    name: string
    enabled: boolean
    window_start_days: number
    window_end_days: number
    max_results: number
  }
  /** False when the named device would not be served this worklist at all. */
  served: boolean
  selected_by: string
  selected_via: string
  /** Why it is not served, when `served` is false. */
  reason: string
  as_device: string
  /** DIMSE status, hex. '0000' is success. */
  status: string
  message: string
  /** The tenancy actually enforced. Empty means the query was NOT scoped. */
  facility_scope: string
  department_scope: string
  truncated: boolean
  count: number
  rows: Record<string, string>[]
}

// --- Worklist items ----------------------------------------------------------

/** *** PHI. *** */
export interface WorklistItem {
  id: number
  origin: string
  external_id: string
  active: boolean
  updated_at: string
  facility_code: string | null
  department_code: string | null
  patient_id: string
  patient_name: string
  patient_birth_date: string | null
  patient_sex: string | null
  accession_number: string | null
  modality: string
  scheduled_station_ae_title: string | null
  scheduled_procedure_step_start_date: string
  scheduled_procedure_step_start_time: string | null
  scheduled_procedure_step_status: string
  requested_procedure_description: string | null
  study_instance_uid: string | null
}

export interface ItemList {
  items: WorklistItem[]
  limit: number
  truncated: boolean
}

// --- Query log ---------------------------------------------------------------

/** One C-FIND, as the daemon recorded it. */
export interface QueryLogRow {
  id: number
  occurred_at: string
  calling_ae: string
  called_ae: string
  source_ip: string
  listener_port: number
  /** Rule ids only. Empty is normal — an AE association involves no rule. */
  matched_rule_ids: string
  worklist_ids: string
  /** How each worklist was chosen: 'ae', 'rule' or 'default', comma-separated.
      Empty means NOTHING selected a worklist — the commonest cause of an
      unexpectedly empty list. */
  selected_by: string
  /** Empty means the query was not scoped to a site. */
  facility_scope: string
  department_scope: string
  matches: number
  truncated: boolean
  cancelled: boolean
  elapsed_ms: number
  /** '0000' success, 'A700' truncated, 'FE00' cancelled, else a failure. */
  dimse_status: string
  /** *** May contain PHI: whatever the modality searched on. *** */
  identifier_summary: string
  message: string
}

export interface QueryLogSummary {
  hours: number
  queries: number
  /** Nonzero means some device is being served an empty list it did not expect. */
  no_rule: number
  empty: number
  failed: number
  truncated: number
  avg_ms: number | null
  max_ms: number
  by_caller: {
    calling_ae: string
    source_ip: string
    queries: number
    no_rule: number
    empty: number
    last_seen: string
  }[]
}

export interface UnregisteredCaller {
  calling_ae: string
  source_ip: string
  listener_port: number
  queries: number
  first_seen: string
  last_seen: string
}

// --- Simulator ---------------------------------------------------------------

/**
 * The answer from `mwl-dicomd --explain` — the daemon's own rule engine.
 *
 * Not a reimplementation. This tool exists to be believed, and a lookalike
 * that agreed 95% of the time would say the rules are fine on the 5% where
 * they are not.
 */
export interface Simulation {
  caller: {
    calling_ae: string
    called_ae: string
    source_ip: string
    listener_port: number
    listener_ae: string
    listener_name: string
    listener_found: boolean
    listener_accepts_find: boolean
    query_policy: string
    may_query: boolean
  }
  device: {
    registered: boolean
    id?: number
    name?: string
    enabled?: boolean
    allow_worklist?: boolean
    modality?: string
    station_name?: string
    location?: string
    facility_id?: number
    facility_code?: string
    primary_department_code?: string
    departments?: string
  }
  /** Every rule considered, in evaluation order, with the clause that rejected it. */
  rules: {
    id: number
    name: string
    priority: number
    matched: boolean
    rejected_on: string
    reason: string
  }[]
  served: {
    /** 'ae' = a direct AE-title association, 'rule' = a worklist rule,
        'default' = the fallback for unmatched callers. */
    selected_by: SelectedBy
    rule_id: number
    /** The AE title that matched, the rule's name, or "default...". */
    rule_name: string
    worklist_id: number
    worklist_name: string
    enabled: boolean
    source?: string
    source_kind?: string
    window_start_days?: number
    window_end_days?: number
    status_filter?: string
    max_results?: number
    scope: {
      facility_code: string
      departments: string
      /** Non-empty means this worklist returns NO rows for this device. */
      unresolved: string
      conflict: boolean
    }
    filters: { owner: string; attribute: string; op: string; value: string }[]
  }[]
  warnings: string[]
}

// --- System ------------------------------------------------------------------

export interface SystemStatus {
  store: {
    reachable: boolean
    target: string
    schema_version: number | null
    latency_ms: number
    error: string
  }
  counts: Record<string, number>
  daemon_binary: string
  daemon_binary_present: boolean
}

export interface ConfigCheck {
  level: 'error' | 'warning'
  title: string
  detail: string
  where: string
}

export interface DicomAttribute {
  keyword: string
  tag: string
  /** Inside the Scheduled Procedure Step Sequence (0040,0100). */
  in_sps: boolean
  match: string
  matchable: boolean
}

export interface Reference {
  attributes: DicomAttribute[]
  match_ops: Record<string, string>
  tokens: Record<string, string>
  sps_statuses: string[]
  query_policies: Record<string, string>
  scopes: Record<string, string>
  transforms: Record<string, string>
}
